import { api } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import type {
  BrandKit,
  CoCompany,
  CoCompanyAccess,
  User,
  XkBrand,
} from '@/lib/api/types';

/**
 * Single-entity GETs return rows even when `status='Deleted'`. Drop them
 * through this guard so the rest of the app treats them as 404.
 */
function assertNotDeleted<T extends { status?: string }>(entity: T, kind: string): T {
  if (entity?.status === 'Deleted') {
    throw new ApiError({ message: `${kind} has been deleted`, status: 404, code: 'NOT_FOUND' });
  }
  return entity;
}

/**
 * Brands & companies API.
 *
 * Backend layout (ox-platform):
 *   GET  /xk-company          → list visible brands (filtered by user's agencyId + xotskiAccess=1)
 *   GET  /xk-company/:id      → single brand
 *   POST /co-companies        → create a co_companies row (used to create a new brand)
 *   PUT  /co-companies/:id    → update branding fields
 *   DELETE /co-companies/:id  → soft delete
 *   POST /co-company-access   → grant a brand access to the user's agency
 *
 * Endpoints `/co-companies/me` and `/co-companies/:id/managers` do NOT yet
 * exist in backend (see plan §"Decisiones pendientes" / gap report).
 */
export const brandsApi = {
  list: () => api.list<XkBrand>('/xk-company'),
  get: (id: number) =>
    api.get<XkBrand>(`/xk-company/${id}`).then((b) => assertNotDeleted(b, 'Brand')),

  create: (input: Partial<CoCompany>) => api.post<XkBrand>('/co-companies', input),
  update: (id: number, patch: Partial<CoCompany>) =>
    api.put<XkBrand>(`/co-companies/${id}`, patch),
  // Soft delete — see eventsApi for the rationale.
  remove: (id: number) =>
    api.put<XkBrand>(`/co-companies/${id}`, { status: 'Deleted' }),

  grantAccess: (input: { companyId: number; agencyId: number; xotskiAccess: boolean }) =>
    api.post<CoCompanyAccess>('/co-company-access', input),
};

/**
 * PRD §8 Tab 1 — CoBrandKit read/upsert. Brand Studio Save & Publish
 * calls `upsert` with the 5-color palette + experience defaults so the
 * kit stays the authoritative source for every bs_event snapshot
 * (associate-survey and reset-identity).
 */
export const brandkitApi = {
  get: (companyId: number, appType: string = 'xotski') =>
    api.get<BrandKit | null>(`/co-brandkits/${companyId}`, {
      params: { appType },
    }),
  upsert: (
    companyId: number,
    patch: Partial<BrandKit> & { appType?: string },
  ) => api.put<BrandKit>(`/co-brandkits/${companyId}`, patch),
};

/** Reads on co_companies (the agency / parent workspace). */
export const companiesApi = {
  /** Currently mocked — `/co-companies/me` does not yet exist in backend. */
  me: () => api.get<CoCompany>('/co-companies/me').then((c) => assertNotDeleted(c, 'Company')),
  get: (id: number) =>
    api.get<CoCompany>(`/co-companies/${id}`).then((c) => assertNotDeleted(c, 'Company')),
  /** Currently mocked — `/co-companies/:id/managers` does not yet exist in backend. */
  managers: (id: number) => api.list<User>(`/co-companies/${id}/managers`),
};
